import React, { useEffect, useState } from "react";
import { paymentsApi } from "../services/payments";
import { t } from "../i18n";
import { T } from "../i18n/T";

// The deferred-Stripe (Ghost DJ, see CLAUDE.md) surfaces on the Profile page:
// the pending balance that drives the one-time welcome banner and the Pending
// Payout widget. Split out of pages/ProfilePage.tsx.

export function usePendingBalance() {
  // Pending balance + welcome banner (deferred-Stripe path). Powers the
  // "Pending Payout" widget above the Payouts section + the one-time welcome
  // banner shown to deferred-Stripe talents on first login. Today that
  // cohort is admin-created Ghost DJ accounts (see CLAUDE.md); self-signup
  // is mandatory-Stripe.
  const [pendingBalance, setPendingBalance] = useState<{
    platformByCurrency: Record<string, number>;
    destinationByCurrency: Record<string, number>;
    platformCount: number;
    destinationCount: number;
    isStripeOnboarded: boolean;
    isAdminCreated: boolean;
    isDeferredStripe?: boolean;
    chosenDeferredStripe?: boolean;
    hasSeenWelcomeBanner: boolean;
  } | null>(null);
  const [welcomeDismissed, setWelcomeDismissed] = useState(false);

  useEffect(() => {
    paymentsApi.pendingBalance()
      .then((r) => setPendingBalance(r))
      .catch(() => { /* non-fatal — banner just won't show */ });
  }, []);

  const dismissWelcome = async () => {
    setWelcomeDismissed(true);
    try {
      await paymentsApi.dismissWelcomeBanner();
    } catch { /* best-effort; banner stays dismissed locally regardless */ }
  };

  // Welcome banner shown ONCE for any deferred-Stripe talent on first login
  // (admin-created Ghost DJ accounts — see CLAUDE.md). Pending balance also
  // drives the "Pending Payout" widget below, so we fetch once and use both.
  // Falls back to the underlying flags so an older server build still drives
  // the banner correctly during a phased deploy.
  const isDeferredTalent = pendingBalance
    ? (pendingBalance.isDeferredStripe ?? (pendingBalance.isAdminCreated || !!pendingBalance.chosenDeferredStripe))
    : false;
  const showWelcome = !!(
    pendingBalance &&
    isDeferredTalent &&
    !pendingBalance.hasSeenWelcomeBanner &&
    !welcomeDismissed
  );
  const totalPendingDollars = pendingBalance
    ? Object.values(pendingBalance.platformByCurrency).reduce((a, b) => a + b, 0) +
      Object.values(pendingBalance.destinationByCurrency).reduce((a, b) => a + b, 0)
    : 0;
  const totalPendingCount = pendingBalance
    ? pendingBalance.platformCount + pendingBalance.destinationCount
    : 0;
  // Platform-held slice only (chargedToPlatform orders — money sitting on
  // the PLATFORM account for deferred-Stripe / Ghost-DJ talents). Ordinary
  // talents are destination-charged, so this stays 0 and the Pending Payout
  // widget stays hidden for them — their spendable money lives on the Balance
  // tab now. Gating the widget on totalPendingDollars would show it to every
  // talent with any pending money, duplicating that surface.
  const platformPendingDollars = pendingBalance
    ? Object.values(pendingBalance.platformByCurrency).reduce((a, b) => a + b, 0)
    : 0;

  return { pendingBalance, showWelcome, dismissWelcome, totalPendingDollars, totalPendingCount, platformPendingDollars };
}

export function WelcomeBanner({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div
      style={{
        background: "rgba(36, 201, 97, 0.09)",
        border: "1px solid rgba(36, 201, 97, 0.35)",
        color: "#e5e7eb",
        padding: "12px 16px",
        borderRadius: 12,
        marginBottom: 12,
        display: "flex",
        alignItems: "center",
        gap: 12,
        fontSize: "0.9rem",
      }}
    >
      <div style={{ flex: 1 }}>
        <T k="pendingPayout.youReAllSet" tags={{ 0: (c) => <strong>{c}</strong> }} />
      </div>
      <button
        onClick={onDismiss}
        aria-label={t("pendingPayout.dismissWelcomeBanner")}
        style={{
          background: "transparent",
          border: "none",
          fontSize: "1.2rem",
          cursor: "pointer",
          opacity: 0.6,
          color: "#9ca3af",
          lineHeight: 1,
        }}
      >
        ✕
      </button>
    </div>
  );
}

type WidgetProps = { totalPendingDollars: number; totalPendingCount: number; onTransfer: () => void };

export function PendingPayoutWidget({ totalPendingDollars, totalPendingCount, onTransfer }: WidgetProps) {
  return (
    <div
      style={{
        background: "rgba(255, 255, 255, 0.04)",
        border: "1px solid rgba(36, 201, 97, 0.35)",
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
        display: "flex",
        alignItems: "center",
        gap: 16,
        flexWrap: "wrap",
      }}
    >
      <div style={{ flex: "1 1 auto", minWidth: 0 }}>
        <div
          style={{
            fontSize: "0.72rem",
            color: "#16a34a",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.6,
            marginBottom: 2,
          }}
        >
          {t("pendingPayout.pendingPayout")}
        </div>
        <div style={{ fontSize: "1.6rem", fontWeight: 800, color: "#fff", lineHeight: 1.1 }}>
          ${totalPendingDollars.toFixed(2)}
        </div>
        <div style={{ fontSize: "0.85rem", color: "#9ca3af", marginTop: 2 }}>
          {t("pendingPayout.fromRequests", { count: totalPendingCount })}
        </div>
      </div>
      <button
        onClick={onTransfer}
        style={{
          background: "linear-gradient(135deg, #24C961, #16a34a)",
          color: "white",
          border: "none",
          padding: "10px 20px",
          borderRadius: 8,
          fontWeight: 700,
          fontSize: "0.92rem",
          cursor: "pointer",
          boxShadow: "0 2px 8px rgba(36,201,97,0.3)",
          flex: "0 0 auto",
        }}
      >
        {t("pendingPayout.transferToBank")}
      </button>
    </div>
  );
}
